import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { useDispatch, useSelector } from "react-redux";
import { useOrder } from "../hook/useOrder";
import { notificationActions } from "../store/notification-slice";
import { useNavigate } from "react-router-dom";

function PayPal() {
  return (
    <PayPalScriptProvider
      options={{
        "client-id": `${process.env.REACT_APP_PAYPAL_CLIENT_ID}`,
        currency: "EUR",
      }}
    >
      <Checkout />
    </PayPalScriptProvider>
  );
}

export default PayPal;

function Checkout() {
  let amountValue = useSelector((state) => state.order.cartItemsTotalPrice);

  const { activatePaidOrder, createPaidOrder } = useOrder();

  let dispatch = useDispatch();

  let navigate = useNavigate();

  return (
    <PayPalButtons
      style={{ layout: "vertical" }}
      createOrder={async (data, actions) => {
        return actions.order.create({
          purchase_units: [
            {
              amount: {
                value: amountValue,
              },
            },
          ],
        });
      }}
      onApprove={async (data, actions) => {
        let capture;

        try {
          if (!actions?.order?.capture) {
            throw new Error("actions.order.capture is not available");
          }

          const details = await actions.order.capture();
          capture = details?.purchase_units?.[0]?.payments?.captures?.[0];
          if (!details?.id || !capture?.id || capture?.status !== "COMPLETED") {
            throw new Error("PayPal capture could not be verified");
          }

          // Only create database orders after PayPal confirms the payment.
          // Cancelling or closing PayPal therefore leaves the cart untouched.
          const registeredOrders = await createPaidOrder();
          if (!registeredOrders) {
            throw new Error("Paid orders could not be created");
          }

          const result = await activatePaidOrder(
            registeredOrders,
            details.id,
            capture.id,
          );

          dispatch(
            notificationActions.setNotification({
              message: `Payment successful. PayPal Order: ${details?.id} (${details?.status})`,
              status: true,
            }),
          );

          if (result) {
            navigate("/orders");
          }
          
          return details;
        } catch (err) {
          const paymentWasCaptured = capture?.status === "COMPLETED";

          dispatch(
            notificationActions.setNotification({
              message: paymentWasCaptured
                ? `The PayPal payment was successful but could not be saved as an order. Please do not pay again; contact support with PayPal capture ID ${capture.id}.`
                : "Payment could not be completed. Please try again.",
              status: false,
            }),
          );

          // PayPal already captured the money. Resolving here prevents the SDK's
          // generic onError handler from replacing the accurate recovery message.
          if (paymentWasCaptured) {
            return;
          }

          throw err;
        }
      }}
      onError={(err) => {
        dispatch(
          notificationActions.setNotification({
            message:
              "PayPal error. Please disable pop-up/tracker blockers or try an incognito window.",
            status: false,
          }),
        );
      }}
      onCancel={(data) => {
        dispatch(
          notificationActions.setNotification({
            message: "Payment cancelled.",
            status: false,
          }),
        );
      }}
    />
  );
}
